import { Box, Card, Flex } from '@chakra-ui/react'
import { createFileRoute } from '@tanstack/react-router'
import {
  applyEdgeChanges,
  applyNodeChanges,
  Background,
  Controls,
  type Edge,
  type EdgeChange,
  MarkerType,
  type Node,
  type NodeChange,
  Position,
  ReactFlow,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { useCallback, useState } from 'react'
import DropNode from '~/components/nodes/datahub/DropNode'
import IncrementCountNode from '~/components/nodes/datahub/IncrementCountNode'
import InputMessageNode from '~/components/nodes/datahub/InputMessageNode'
import OutputMessageNode from '~/components/nodes/datahub/OutputMessageNode'
import SchemaValidationNode from '~/components/nodes/datahub/SchemaValidationNode'
import TransformationNode from '~/components/nodes/datahub/TransformationNode'
import { PageHeader } from '~/components/shell'

export const Route = createFileRoute('/_app/dhgeolocationflow')({
  component: DHGeolocationFlowPage,
})

const schema = `
\`\`\`json
{
  "title": "USA Location Data",
  "description": "A schema that matches any objects with longitude and latitude coordinates near the USA",
  "required": [
    "latitude",
    "longitude"
  ],
  "type": "object",
  "properties": {
    "latitude": {
      "type": "number",
      "minimum": 20,
      "maximum": 50
    },
    "longitude": {
      "type": "number",
      "minimum": -130,
      "maximum": -60
    }
  }
}
`

const script = `
\`\`\`typescript
    function convert(fahrenheit) {
        return Math.floor((fahrenheit - 32) * 5/9);
    }

    function transform(publish, context) {
        publish.payload = {
            "celsius": convert(publish.payload.fahrenheit),
            "timestamp": publish.payload.timestamp
        }

        return publish;
    }
`

const inputMessage = `
\`\`\`json
{
"latitude": 33,
"longitude": -100
}
`

const outputMessage = `
\`\`\`json
{
"latitude": 33,
"longitude": -100
}
`

const initNodes: Node[] = [
  // Input
  {
    id: '1',
    data: {
      label: 'InputMessage',
      topic: 'HPSCorp/Vans/VIN-RT3467F28J/Location',
      message: inputMessage,
    },
    type: 'inputMessageNode',
    sourcePosition: Position.Right,
    targetPosition: Position.Right,
    position: { x: 0, y: 100 },
  },

  // DataHub
  {
    id: '2',
    data: { label: 'Schema Validation', schema: schema },
    type: 'schemaValidationNode',
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
    position: { x: 500, y: 0 },
  },

  // Bad path
  {
    id: '3',
    data: {
      label: 'Log Error',
      logCounter: 'TemperatureSchemaValidationError',
      message: 'Schema validation failed',
    },
    type: 'incrementCountNode',
    sourcePosition: Position.Right,
    targetPosition: Position.Top,
    position: { x: 600, y: 700 },
  },
  {
    id: '4',
    data: { label: 'Drop', script: script },
    type: 'dropNode',
    targetPosition: Position.Left,
    position: { x: 1000, y: 750 },
  },

  // Good path - Output
  {
    id: '5',
    data: {
      label: 'OutputMessage',
      topic: 'HPSCorp/Vans/VIN-RT3467F28J/Location',
      message: outputMessage,
    },
    type: 'outputMessageNode',
    targetPosition: Position.Left,
    sourcePosition: Position.Left,
    position: { x: 1600, y: 100 },
  },
]

const initEdges: Edge[] = [
  {
    id: '12',
    source: '1',
    target: '2',
    label: 'De-serialize',
    style: { stroke: '#037DA5', strokeWidth: 5 },
    animated: true,
    markerEnd: { type: MarkerType.ArrowClosed, color: '#037DA5' },
  },
  {
    id: '23',
    source: '2',
    sourceHandle: 'b',
    target: '3',
    label: 'Error',
    style: { stroke: '#e91e63', strokeWidth: 5 },
    animated: true,
    markerEnd: { type: MarkerType.ArrowClosed, color: '#e91e63' },
  },
  {
    id: '34',
    source: '3',
    target: '4',
    label: 'Cleaning',
    style: { stroke: '#037DA5', strokeWidth: 5 },
    animated: true,
    markerEnd: { type: MarkerType.ArrowClosed, color: '#037DA5' },
  },
  {
    id: '2a5',
    source: '2',
    sourceHandle: 'a',
    target: '5',
    label: 'Success',
    style: { stroke: '#10b981', strokeWidth: 5 },
    animated: true,
    markerEnd: { type: MarkerType.ArrowClosed, color: '#10b981' },
  },
]

const nodeTypes = {
  inputMessageNode: InputMessageNode,
  schemaValidationNode: SchemaValidationNode,
  incrementCountNode: IncrementCountNode,
  transformationNode: TransformationNode,
  outputMessageNode: OutputMessageNode,
  dropNode: DropNode,
}

function DHGeolocationFlowPage() {
  const [nodes, setNodes] = useState<Node[]>(initNodes)
  const [edges, setEdges] = useState<Edge[]>(initEdges)

  const onNodesChange = useCallback(
    (changes: NodeChange[]) => setNodes((nds) => applyNodeChanges(changes, nds)),
    [],
  )
  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) => setEdges((eds) => applyEdgeChanges(changes, eds)),
    [],
  )

  return (
    <Flex direction="column" h="100%" gap={4}>
      <PageHeader
        title="Geolocation Validation Flow"
        subtitle="HiveMQ DataHub pipeline for validating location data within USA boundaries"
      />

      <Card.Root flex={1} minH={0} overflow="hidden">
        <Box h="100%" w="100%">
          <ReactFlow
            nodes={nodes}
            onNodesChange={onNodesChange}
            edges={edges}
            onEdgesChange={onEdgesChange}
            nodeTypes={nodeTypes}
            fitView
            className="flow-customization"
          >
            <Background />
            <Controls />
          </ReactFlow>
        </Box>
      </Card.Root>
    </Flex>
  )
}
